import { useState, useEffect, useRef, useCallback } from 'react';
import Draggable from 'react-draggable';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useSessionStore } from '@/state/session-store';
import { useApi } from '@/lib/api';
import { AgendaInput } from '@/components/AgendaInput';
import { AgendaPanel } from '@/components/AgendaPanel';
import { SessionSummary } from '@/components/SessionSummary';
import { StatusBar } from '@/components/StatusBar';
import { DeepgramSpeechService } from '@/services/deepgram-speech';
import { MatchingEngine, CustomSynonyms } from '@/services/matching-engine';
import { expandAgendaItems, mergeExpansionIntoAgenda } from '@/services/agenda-expander';
import { buildSessionFeedback, saveFeedbackSession, isFeedbackEnabled } from '@/services/feedback-collector';
import { fetchOrgVocabulary } from '@/services/vocabulary-service';
import { SessionSetup, CoachingPanel, extractActionItems } from '@/components/coaching';
import { RehearsalSetup, QuestionCard, RehearsalSummary, AnswerInputModal } from '@/components/rehearsal';
import { useBroadcastChannel, openSessionPopup, isPopupOpen } from '@/hooks/useBroadcastChannel';
import { detectAnswerTrigger, isSubstantiveText, seemsComplete } from '@/services/answer-trigger-detector';
import type { TranscriptSegment, RehearsalQuestion } from '@/types';

// Silence threshold for auto-submitting voice answers (2 seconds)
const VOICE_ANSWER_SILENCE_THRESHOLD_MS = 2000;

// Two-pass matching: sweep interval in milliseconds (60 seconds)
const SWEEP_INTERVAL_MS = 60000;

// Max call time check interval (every 30 seconds)
const MAX_CALL_TIME_CHECK_MS = 30000;

// Idle timeout: end session after no speech detected (5 minutes)
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
// Warning period before auto-ending (1 minute before)
const IDLE_WARNING_BEFORE_MS = 60 * 1000;
// How often to check for idle (every 30 seconds)
const IDLE_CHECK_INTERVAL_MS = 30000;

export function FloatingWidget() {
  const [minimized, setMinimized] = useState(false);
  const [size, setSize] = useState({
    width: 700,
    height: window.innerHeight - 100
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [maxCallTimeWarning, setMaxCallTimeWarning] = useState(false);
  const [idleTimeoutWarning, setIdleTimeoutWarning] = useState(false);
  const [showTypedAnswerModal, setShowTypedAnswerModal] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);
  const speechServiceRef = useRef<DeepgramSpeechService | null>(null);
  const matchingEngineRef = useRef<MatchingEngine | null>(null);
  const sweepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxCallTimeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimeoutIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastSpeechTimestampRef = useRef<number>(Date.now());
  const orgVocabRef = useRef<CustomSynonyms>({});
  const voiceAnswerSilenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isSignedIn, getToken } = useAuth();
  const { organization } = useOrganization();
  const { post, patch } = useApi();

  const {
    status,
    agenda,
    error,
    connectionStatus,
    startSession,
    endSession,
    resetSession,
    addTranscriptSegment,
    markItemCovered,
    markItemCoveredBySweep,
    setError,
    setConnectionStatus,
    setLlmExpansionUsed,
    setSessionId,
    llmExpansionUsed,
    sweepMatchCount,
    startedAt,
    transcript,
    // Coaching state and actions
    coachingSettings,
    addActionItem,
    addSpeakerTalkTime,
    addWPMSegment,
    // Rehearsal mode state and actions
    isRehearsalMode,
    rehearsalSettings,
    rehearsalSessionId,
    currentQuestion,
    questionHistory,
    isGeneratingQuestion,
    isEvaluatingAnswer,
    setRehearsalSessionId,
    setCurrentQuestion,
    addToQuestionHistory,
    updateQuestionInHistory,
    setGeneratingQuestion,
    setEvaluatingAnswer,
    disableRehearsalMode,
    // Methodology selection
    selectedMethodologyId,
    // Phase 2: Voice answer state and actions
    isListeningForAnswer,
    answerTranscriptBuffer,
    startAnswerListening,
    stopAnswerListening,
    appendToAnswerBuffer,
    clearAnswerBuffer,
    getAnswerBuffer,
  } = useSessionStore();

  // Get syncable state for broadcasting
  const getSyncableState = useSessionStore(state => state.getSyncableState);

  // Broadcast channel for popup window sync
  const { broadcast } = useBroadcastChannel({
    source: 'main',
    onMessage: (msg) => {
      // Handle messages from popup
      if (msg.type === 'POPUP_ACTION') {
        const action = msg.payload as { action: string };
        if (action.action === 'end_session') {
          // Popup requested session end
          handleEndSessionRef.current?.();
        }
      }
      // When popup is ready, send current state
      if (msg.type === 'POPUP_READY') {
        broadcast('STATE_UPDATE', getSyncableState());
      }
    },
  });

  // Ref to handle circular dependency with handleEndSession
  const handleEndSessionRef = useRef<(() => void) | null>(null);
  // Ref to handle circular dependency with generateQuestionForItem
  const generateQuestionForItemRef = useRef<((coveredItemId: string) => void) | null>(null);
  // Ref to handle circular dependency with submitAnswer
  const submitAnswerRef = useRef<((mode: 'voice' | 'typed') => void) | null>(null);

  const handleTranscriptSegment = useCallback((segment: TranscriptSegment) => {
    addTranscriptSegment(segment);

    // Update last speech timestamp when we receive a final segment with actual text
    // This is used for idle timeout detection
    if (segment.isFinal && segment.text.trim().length > 0) {
      lastSpeechTimestampRef.current = Date.now();
      // Clear idle warning if user starts speaking again
      setIdleTimeoutWarning(false);
    }

    // Phase 2: Voice answer detection when in rehearsal mode with a question
    const state = useSessionStore.getState();
    if (state.isRehearsalMode && state.currentQuestion && !state.currentQuestion.respondedAt) {
      // Check for trigger phrase
      if (!state.isListeningForAnswer) {
        const triggerResult = detectAnswerTrigger(segment.text);
        if (triggerResult.triggered) {
          console.log('🎤 Voice answer trigger detected:', triggerResult.triggerPhrase);
          startAnswerListening();
          if (triggerResult.answerText) {
            appendToAnswerBuffer(triggerResult.answerText + ' ');
          }
        }
      } else if (segment.isFinal) {
        // Already listening, append transcript to buffer
        appendToAnswerBuffer(segment.text + ' ');

        // Reset silence timer - auto-submit after 2s of silence
        if (voiceAnswerSilenceTimerRef.current) {
          clearTimeout(voiceAnswerSilenceTimerRef.current);
        }

        // Check if answer seems complete
        const buffer = state.getAnswerBuffer();
        if (isSubstantiveText(buffer) && seemsComplete(buffer)) {
          voiceAnswerSilenceTimerRef.current = setTimeout(() => {
            // Auto-submit after silence
            const finalBuffer = useSessionStore.getState().getAnswerBuffer();
            if (finalBuffer.trim()) {
              console.log('🎤 Auto-submitting voice answer after silence');
              submitAnswerRef.current?.('voice');
            }
          }, VOICE_ANSWER_SILENCE_THRESHOLD_MS);
        }
      }
    }

    if (segment.isFinal && matchingEngineRef.current) {
      const matches = matchingEngineRef.current.processSegment(segment);
      for (const match of matches) {
        // Check if item is already covered before marking
        const currentAgenda = useSessionStore.getState().agenda;
        const item = currentAgenda?.items.find(i => i.id === match.itemId);
        const wasAlreadyCovered = item?.covered;

        markItemCovered(match.itemId, match.confidence, match.matchedText);

        // Rehearsal: Generate question for newly covered item
        if (!wasAlreadyCovered && useSessionStore.getState().isRehearsalMode) {
          // Small delay to let the match settle, then generate question
          setTimeout(() => {
            generateQuestionForItemRef.current?.(match.itemId);
          }, 500);
        }
      }

      // Coaching: Extract action items from transcript
      if (coachingSettings.enableActionCapture) {
        const elapsedSec = startedAt ? (Date.now() - startedAt) / 1000 : 0;
        const actionItems = extractActionItems(
          segment.text,
          elapsedSec,
          segment.speaker ?? 0,
          {
            includeProspectActions: coachingSettings.includeProspectActions,
            customTriggerPhrase: coachingSettings.actionTriggerPhrase,
          }
        );
        for (const item of actionItems) {
          addActionItem(item);
        }
      }

      // Coaching: Update per-speaker talk time
      // Speaker ID comes from Deepgram diarization (arbitrary assignment)
      // User can swap which speaker is "You" via the CoachingPanel
      if (segment.duration) {
        const durationMs = segment.duration * 1000;
        const speakerId = segment.speaker ?? 0;
        addSpeakerTalkTime(speakerId, durationMs);
      }

      // Coaching: Update WPM
      // All captured speech is user's speech in single-mic setup
      if (segment.duration) {
        const words = segment.text.split(/\s+/).filter(w => w.length > 0).length;
        const durationMs = segment.duration * 1000;
        addWPMSegment(words, durationMs);
      }
    }
  }, [addTranscriptSegment, markItemCovered, coachingSettings, startedAt, addActionItem, addSpeakerTalkTime, addWPMSegment, startAnswerListening, appendToAnswerBuffer]);

  const handleSpeechError = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, [setError]);

  const handleConnectionStatus = useCallback((status: 'connecting' | 'connected' | 'disconnected') => {
    setConnectionStatus(status);
  }, [setConnectionStatus]);

  // Rehearsal: Generate a question when a talking point is covered
  const generateQuestionForItem = useCallback(async (coveredItemId: string) => {
    if (!isRehearsalMode || !rehearsalSessionId || isGeneratingQuestion) return;

    // Skip question generation if "no questions" mode is enabled
    const currentSettings = useSessionStore.getState().rehearsalSettings;
    if (currentSettings?.noQuestions) return;

    const currentAgenda = useSessionStore.getState().agenda;
    const currentTranscript = useSessionStore.getState().transcript;
    if (!currentAgenda) return;

    const coveredItem = currentAgenda.items.find(i => i.id === coveredItemId);
    if (!coveredItem || coveredItem.isGroupHeader) return;

    // Get recent transcript (last 90 seconds)
    const recentSegments = currentTranscript
      .filter(s => s.isFinal)
      .slice(-20);
    const recentTranscript = recentSegments.map(s => s.text).join(' ');

    if (!recentTranscript.trim()) return;

    setGeneratingQuestion(true);
    try {
      const authToken = await getToken();
      const response = await fetch(`/api/rehearsals/${rehearsalSessionId}/questions/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          recentTranscript,
          coveredItemId: coveredItem.id,
          coveredItemText: coveredItem.text,
        }),
      });

      if (response.ok) {
        const question: RehearsalQuestion = await response.json();
        setCurrentQuestion(question);
        addToQuestionHistory(question);
      }
    } catch (err) {
      console.warn('Failed to generate rehearsal question:', err);
    } finally {
      setGeneratingQuestion(false);
    }
  }, [isRehearsalMode, rehearsalSessionId, isGeneratingQuestion, getToken, setGeneratingQuestion, setCurrentQuestion, addToQuestionHistory]);

  // Rehearsal: Submit an answer and get it evaluated
  const submitAnswer = useCallback(async (answer: string, answerMode: 'voice' | 'typed' = 'typed') => {
    if (!currentQuestion || !rehearsalSessionId || isEvaluatingAnswer) return;

    // Clear any pending voice answer timer
    if (voiceAnswerSilenceTimerRef.current) {
      clearTimeout(voiceAnswerSilenceTimerRef.current);
      voiceAnswerSilenceTimerRef.current = null;
    }

    // For voice answers, get the buffer
    const answerText = answerMode === 'voice' ? getAnswerBuffer().trim() : answer;
    if (!answerText) return;

    // Calculate response time
    const responseTimeMs = currentQuestion.askedAt ? Date.now() - currentQuestion.askedAt : undefined;

    // Clear voice state if answering by voice
    if (answerMode === 'voice') {
      stopAnswerListening();
      clearAnswerBuffer();
    }

    setEvaluatingAnswer(true);
    try {
      const authToken = await getToken();
      const response = await fetch(`/api/rehearsals/${rehearsalSessionId}/questions/${currentQuestion.id}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          answer: answerText,
          answerMode,
          responseTimeMs,
        }),
      });

      if (response.ok) {
        const updatedQuestion: RehearsalQuestion = await response.json();
        updateQuestionInHistory(currentQuestion.id, updatedQuestion);
        // Don't clear current question - let the QuestionCard show the result
      }
    } catch (err) {
      console.warn('Failed to submit answer:', err);
      setError('Failed to evaluate answer. Please try again.');
    } finally {
      setEvaluatingAnswer(false);
    }
  }, [currentQuestion, rehearsalSessionId, isEvaluatingAnswer, getToken, setEvaluatingAnswer, updateQuestionInHistory, setError, getAnswerBuffer, stopAnswerListening, clearAnswerBuffer]);

  // Rehearsal: Skip a question
  const skipQuestion = useCallback(async (reason: 'manual' | 'timeout' = 'manual') => {
    if (!currentQuestion || !rehearsalSessionId) return;

    // Clear any pending voice answer timer
    if (voiceAnswerSilenceTimerRef.current) {
      clearTimeout(voiceAnswerSilenceTimerRef.current);
      voiceAnswerSilenceTimerRef.current = null;
    }

    // Clear voice state
    stopAnswerListening();
    clearAnswerBuffer();

    // Get timer duration for tracking
    const timerDurationMs = rehearsalSettings?.difficulty === 'medium' ? 15000
      : rehearsalSettings?.difficulty === 'hard' ? 10000
      : undefined;

    try {
      const authToken = await getToken();
      const response = await fetch(`/api/rehearsals/${rehearsalSessionId}/questions/${currentQuestion.id}/skip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          reason,
          timerDurationMs,
        }),
      });

      if (response.ok) {
        const updatedQuestion: RehearsalQuestion = await response.json();
        updateQuestionInHistory(currentQuestion.id, updatedQuestion);
        setCurrentQuestion(null); // Move to next question
      }
    } catch (err) {
      console.warn('Failed to skip question:', err);
    }
  }, [currentQuestion, rehearsalSessionId, getToken, updateQuestionInHistory, setCurrentQuestion, stopAnswerListening, clearAnswerBuffer, rehearsalSettings]);

  // Rehearsal: Dismiss current question result and continue
  const dismissQuestionResult = useCallback(() => {
    setCurrentQuestion(null);
  }, [setCurrentQuestion]);

  const handleStartSession = useCallback(async () => {
    if (!agenda) return;

    // Step 1: Expand agenda with LLM (non-blocking, enhances matching)
    setIsExpanding(true);
    let expandedItems = agenda.items;

    try {
      const expansions = await expandAgendaItems(agenda.items);
      if (expansions.size > 0) {
        expandedItems = mergeExpansionIntoAgenda(agenda.items, expansions);
        setLlmExpansionUsed(true);
        console.log(`✅ Merged LLM expansions into ${expansions.size} items`);
      }
    } catch (err) {
      console.warn('Agenda expansion failed, continuing with basic matching:', err);
    } finally {
      setIsExpanding(false);
    }

    // Step 2: Fetch org vocabulary for custom synonyms (if in org context)
    let authToken: string | null | undefined;
    if (isSignedIn) {
      authToken = await getToken();
      if (organization?.id) {
        try {
          orgVocabRef.current = await fetchOrgVocabulary(authToken, organization.id);
        } catch (err) {
          console.warn('Failed to fetch org vocabulary:', err);
          orgVocabRef.current = {};
        }
      }
    }

    // Step 3: Create session in database (if signed in)
    if (isSignedIn) {
      try {
        const agendaForApi = agenda.items.map(item => ({
          id: item.id,
          text: item.text,
          isGroupHeader: item.isGroupHeader || false,
        }));
        const response = await post<{ id: string }>('/sessions', {
          title: `Session ${new Date().toLocaleDateString()}`,
          agenda: agendaForApi,
          ...(selectedMethodologyId && {
            mode: 'methodology',
            methodologyId: selectedMethodologyId,
          }),
        });
        setSessionId(response.id);
        console.log(`✅ Created session in database: ${response.id}`);

        // Step 3b: Create rehearsal session if in rehearsal mode
        if (isRehearsalMode && rehearsalSettings) {
          try {
            const rehearsalResponse = await post<{ id: string }>('/rehearsals', {
              sessionId: response.id,
              difficulty: rehearsalSettings.difficulty,
              questionFocus: rehearsalSettings.questionFocus,
              agenda: agendaForApi,
            });
            setRehearsalSessionId(rehearsalResponse.id);
            console.log(`✅ Created rehearsal session: ${rehearsalResponse.id}`);
          } catch (err) {
            console.warn('Failed to create rehearsal session:', err);
            // Continue anyway - can still practice without scoring
          }
        }
      } catch (err) {
        console.warn('Failed to create session in database:', err);
        // Continue anyway - session still works locally
      }
    }

    // Step 4: Create matching engine with expanded items and org vocabulary
    matchingEngineRef.current = new MatchingEngine(expandedItems, orgVocabRef.current);

    // Step 5: Set up two-pass matching sweep interval
    sweepIntervalRef.current = setInterval(() => {
      if (matchingEngineRef.current) {
        console.log('⏰ Running periodic transcript sweep...');
        const sweepMatches = matchingEngineRef.current.runFullTranscriptSweep();
        for (const match of sweepMatches) {
          markItemCoveredBySweep(match.itemId, match.confidence, match.matchedText);
        }
      }
    }, SWEEP_INTERVAL_MS);

    // Step 6: Set up max call time check
    const maxCallTimeMs = coachingSettings.maxCallTimeMin * 60 * 1000;
    if (maxCallTimeMs > 0) {
      maxCallTimeIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - (useSessionStore.getState().startedAt || Date.now());
        const remaining = maxCallTimeMs - elapsed;

        // Show warning 2 minutes before auto-end
        if (remaining <= 120000 && remaining > 0) {
          setMaxCallTimeWarning(true);
        }

        // Auto-end session when time is up
        if (remaining <= 0) {
          console.log('⏰ Max call time reached, auto-ending session');
          handleEndSession();
        }
      }, MAX_CALL_TIME_CHECK_MS);
    }

    // Step 6b: Set up idle timeout check (no speech for 5 minutes = end session)
    // Initialize last speech timestamp to now
    lastSpeechTimestampRef.current = Date.now();
    idleTimeoutIntervalRef.current = setInterval(() => {
      const timeSinceLastSpeech = Date.now() - lastSpeechTimestampRef.current;

      // Show warning 1 minute before auto-end
      if (timeSinceLastSpeech >= IDLE_TIMEOUT_MS - IDLE_WARNING_BEFORE_MS && timeSinceLastSpeech < IDLE_TIMEOUT_MS) {
        setIdleTimeoutWarning(true);
      }

      // Auto-end session if no speech for too long
      if (timeSinceLastSpeech >= IDLE_TIMEOUT_MS) {
        console.log('💤 Idle timeout reached (no speech for 5 minutes), auto-ending session');
        handleEndSession();
      }
    }, IDLE_CHECK_INTERVAL_MS);

    // Step 7: Start speech service
    speechServiceRef.current = new DeepgramSpeechService(
      handleTranscriptSegment,
      handleSpeechError,
      handleConnectionStatus
    );

    startSession();

    // Broadcast session start to popup with full state
    // Small delay to ensure store is updated
    setTimeout(() => {
      broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());
    }, 50);

    try {
      await speechServiceRef.current.start(authToken ?? undefined);
    } catch {
      // Error already handled in service
    }
  }, [agenda, startSession, handleTranscriptSegment, handleSpeechError, handleConnectionStatus, markItemCoveredBySweep, setLlmExpansionUsed, isSignedIn, getToken, post, setSessionId, organization, coachingSettings.maxCallTimeMin, broadcast, isRehearsalMode, rehearsalSettings, setRehearsalSessionId, selectedMethodologyId]);

  const handleEndSession = useCallback(async () => {
    // Run final sweep before ending
    if (matchingEngineRef.current) {
      console.log('🏁 Running final transcript sweep before ending...');
      const finalMatches = matchingEngineRef.current.runFullTranscriptSweep();
      for (const match of finalMatches) {
        markItemCoveredBySweep(match.itemId, match.confidence, match.matchedText);
      }
    }

    // Clear sweep interval
    if (sweepIntervalRef.current) {
      clearInterval(sweepIntervalRef.current);
      sweepIntervalRef.current = null;
    }

    // Clear max call time interval
    if (maxCallTimeIntervalRef.current) {
      clearInterval(maxCallTimeIntervalRef.current);
      maxCallTimeIntervalRef.current = null;
    }
    setMaxCallTimeWarning(false);

    // Clear idle timeout interval
    if (idleTimeoutIntervalRef.current) {
      clearInterval(idleTimeoutIntervalRef.current);
      idleTimeoutIntervalRef.current = null;
    }
    setIdleTimeoutWarning(false);

    speechServiceRef.current?.stop();
    speechServiceRef.current = null;

    // Capture current state before ending
    const storeState = useSessionStore.getState();
    const currentAgenda = storeState.agenda;
    const currentSessionId = storeState.sessionId;
    const currentTalkRatio = storeState.talkRatio;
    const currentSpeakerTalkTime = storeState.speakerTalkTime;
    const currentSpeakerLabels = storeState.speakerLabels;
    const currentUserSpeakerId = storeState.userSpeakerId ?? 0;
    const currentWpmSegments = storeState.wpmSegments;
    const currentActionItems = storeState.actionItems;
    const currentCoachingAlerts = storeState.coachingAlerts;

    // Broadcast session end to popup (with session ID for redirect)
    broadcast('SESSION_END', { sessionId: currentSessionId });

    endSession();

    // Save session results to database (if signed in and we have a session ID)
    if (isSignedIn && currentSessionId && currentAgenda) {
      try {
        const trackableItems = currentAgenda.items.filter(item => !item.isGroupHeader);
        const coveredItems = trackableItems.filter(item => item.covered);
        const coverageScore = trackableItems.length > 0
          ? Math.round((coveredItems.length / trackableItems.length) * 100)
          : 0;

        // Calculate performance stats for metadata
        const totalTalkTime = currentTalkRatio.userTalkTimeMs + currentTalkRatio.otherTalkTimeMs;
        const userTalkRatioPct = totalTalkTime > 0
          ? Math.round((currentTalkRatio.userTalkTimeMs / totalTalkTime) * 100)
          : 50;

        // Calculate average WPM from segments
        const totalWords = currentWpmSegments.reduce((sum, s) => sum + s.words, 0);
        const totalWpmDuration = currentWpmSegments.reduce((sum, s) => sum + s.durationMs, 0);
        const avgWpm = totalWpmDuration > 0 ? Math.round((totalWords / totalWpmDuration) * 60000) : 0;

        // Build speaker stats
        const speakerStats: Record<string, { label: string; talkTimeMs: number; talkRatioPct: number }> = {};
        for (const [speakerId, talkTimeMs] of Object.entries(currentSpeakerTalkTime)) {
          const id = Number(speakerId);
          speakerStats[speakerId] = {
            label: currentSpeakerLabels[id] || (id === currentUserSpeakerId ? 'You' : `Speaker ${id + 1}`),
            talkTimeMs,
            talkRatioPct: totalTalkTime > 0 ? Math.round((talkTimeMs / totalTalkTime) * 100) : 0,
          };
        }

        // Build metadata with performance stats
        const metadata = {
          performanceStats: {
            avgWpm,
            userTalkRatioPct,
            totalWords,
            totalTalkTimeMs: totalTalkTime,
            speakerCount: Object.keys(currentSpeakerTalkTime).length,
            speakerStats,
          },
          actionItems: currentActionItems.map(item => ({
            id: item.id,
            text: item.text,
            owner: item.speaker,
            detectedAt: item.timestamp,
            completed: item.completed,
          })),
          coachingAlerts: currentCoachingAlerts.map(alert => ({
            type: alert.type,
            message: alert.message,
            timestamp: alert.timestamp,
            value: alert.value,
          })),
        };

        await patch(`/sessions/${currentSessionId}`, {
          status: 'complete',
          endedAt: Date.now(),
          coverageScore,
          itemsCovered: coveredItems.length,
          metadata, // Keep for backwards compatibility
        });
        console.log(`✅ Saved session results to database: ${coverageScore}% coverage, ${avgWpm} WPM, ${userTalkRatioPct}% talk ratio`);

        // Save metrics to new normalized tables
        await post(`/sessions/${currentSessionId}/metrics`, {
          performanceStats: {
            avgWpm,
            userTalkRatioPct,
            totalTalkTimeMs: totalTalkTime,
            totalWords,
            speakerCount: Object.keys(currentSpeakerTalkTime).length,
            speakerStats,
          },
          actionItems: currentActionItems.map(item => ({
            text: item.text,
            owner: item.speaker,
            detectedAtSec: item.timestamp ? Math.round(item.timestamp / 1000) : undefined,
            completed: item.completed,
          })),
          coachingAlerts: currentCoachingAlerts.map(alert => ({
            alertType: alert.type,
            message: alert.message,
            triggeredAt: alert.timestamp,
            value: alert.value,
          })),
        });
        console.log(`✅ Saved metrics to normalized tables`);

        // Also save transcript (for team tier - server will check permissions)
        if (transcript.length > 0) {
          const finalSegments = transcript.filter(s => s.isFinal);
          const wordCount = finalSegments.reduce(
            (sum, s) => sum + s.text.split(/\s+/).filter(w => w.length > 0).length,
            0
          );
          await post(`/sessions/${currentSessionId}/transcript`, {
            segments: finalSegments,
            wordCount,
          });
          console.log(`✅ Saved transcript: ${wordCount} words`);
        }

        // Complete rehearsal session if in rehearsal mode
        const currentRehearsalSessionId = useSessionStore.getState().rehearsalSessionId;
        if (currentRehearsalSessionId) {
          try {
            await patch(`/rehearsals/${currentRehearsalSessionId}`, {
              status: 'completed',
            });
            console.log(`✅ Completed rehearsal session: ${currentRehearsalSessionId}`);
          } catch (err) {
            console.warn('Failed to complete rehearsal session:', err);
          }
        }
      } catch (err) {
        console.warn('Failed to save session results:', err);
        // Non-critical - session still completed locally
      }
    }

    // Collect feedback after session ends (async, non-blocking)
    setTimeout(() => {
      if (isFeedbackEnabled() && agenda && startedAt) {
        const transcriptWords = transcript
          .filter(s => s.isFinal)
          .map(s => s.text.toLowerCase().split(/\s+/))
          .flat();

        const feedback = buildSessionFeedback(
          agenda,
          transcriptWords,
          startedAt,
          llmExpansionUsed,
          sweepMatchCount
        );
        saveFeedbackSession(feedback);
      }
    }, 100);
  }, [endSession, markItemCoveredBySweep, agenda, startedAt, transcript, llmExpansionUsed, sweepMatchCount, isSignedIn, patch, post, broadcast]);

  // Update ref for circular dependency with handleStartSession
  useEffect(() => {
    handleEndSessionRef.current = handleEndSession;
  }, [handleEndSession]);

  // Update ref for circular dependency with handleTranscriptSegment
  useEffect(() => {
    generateQuestionForItemRef.current = generateQuestionForItem;
  }, [generateQuestionForItem]);

  // Update ref for circular dependency with voice answer auto-submit
  useEffect(() => {
    submitAnswerRef.current = (mode) => submitAnswer('', mode);
  }, [submitAnswer]);

  const handleReset = useCallback(() => {
    // Clear sweep interval
    if (sweepIntervalRef.current) {
      clearInterval(sweepIntervalRef.current);
      sweepIntervalRef.current = null;
    }

    // Clear max call time interval
    if (maxCallTimeIntervalRef.current) {
      clearInterval(maxCallTimeIntervalRef.current);
      maxCallTimeIntervalRef.current = null;
    }
    setMaxCallTimeWarning(false);

    speechServiceRef.current?.destroy();
    speechServiceRef.current = null;
    matchingEngineRef.current = null;
    orgVocabRef.current = {};
    setShowSetup(false);
    resetSession();

    // Broadcast reset to popup
    broadcast('SESSION_RESET', {});
  }, [resetSession, broadcast]);

  useEffect(() => {
    return () => {
      speechServiceRef.current?.destroy();
      if (sweepIntervalRef.current) {
        clearInterval(sweepIntervalRef.current);
      }
      if (maxCallTimeIntervalRef.current) {
        clearInterval(maxCallTimeIntervalRef.current);
      }
      if (idleTimeoutIntervalRef.current) {
        clearInterval(idleTimeoutIntervalRef.current);
      }
    };
  }, []);

  // Initialize session if widget mounts with status='listening' but no speech service
  // This happens when SessionEditor calls startSession() before widget is mounted
  useEffect(() => {
    if (status === 'listening' && !speechServiceRef.current && agenda) {
      console.log('🔧 FloatingWidget mounted with active session, initializing...');

      const initializeSession = async () => {
        // Signal that we're starting initialization
        setConnectionStatus('connecting');

        // Step 1: Expand agenda with LLM (non-blocking)
        setIsExpanding(true);
        let expandedItems = agenda.items;

        try {
          const expansions = await expandAgendaItems(agenda.items);
          if (expansions.size > 0) {
            expandedItems = mergeExpansionIntoAgenda(agenda.items, expansions);
            setLlmExpansionUsed(true);
            console.log(`✅ Merged LLM expansions into ${expansions.size} items`);
          }
        } catch (err) {
          console.warn('Agenda expansion failed, continuing with basic matching:', err);
        } finally {
          setIsExpanding(false);
        }

        // Step 2: Fetch org vocabulary
        let authToken: string | null | undefined;
        if (isSignedIn) {
          authToken = await getToken();
          if (organization?.id) {
            try {
              orgVocabRef.current = await fetchOrgVocabulary(authToken, organization.id);
            } catch (err) {
              console.warn('Failed to fetch org vocabulary:', err);
              orgVocabRef.current = {};
            }
          }
        }

        // Step 3: Create session in database (if signed in and no session ID yet)
        const currentSessionId = useSessionStore.getState().sessionId;
        if (isSignedIn && !currentSessionId) {
          try {
            const agendaForApi = agenda.items.map(item => ({
              id: item.id,
              text: item.text,
              isGroupHeader: item.isGroupHeader || false,
            }));
            const currentMethodologyId = useSessionStore.getState().selectedMethodologyId;
            const response = await post<{ id: string }>('/sessions', {
              title: `Session ${new Date().toLocaleDateString()}`,
              agenda: agendaForApi,
              ...(currentMethodologyId && {
                mode: 'methodology',
                methodologyId: currentMethodologyId,
              }),
            });
            setSessionId(response.id);
            console.log(`✅ Created session in database: ${response.id}`);

            // Create rehearsal session if in rehearsal mode
            if (isRehearsalMode && rehearsalSettings) {
              try {
                const rehearsalResponse = await post<{ id: string }>('/rehearsals', {
                  sessionId: response.id,
                  difficulty: rehearsalSettings.difficulty,
                  questionFocus: rehearsalSettings.questionFocus,
                  agenda: agendaForApi,
                });
                setRehearsalSessionId(rehearsalResponse.id);
                console.log(`✅ Created rehearsal session: ${rehearsalResponse.id}`);
              } catch (err) {
                console.warn('Failed to create rehearsal session:', err);
              }
            }
          } catch (err) {
            console.warn('Failed to create session in database:', err);
          }
        }

        // Step 4: Create matching engine
        matchingEngineRef.current = new MatchingEngine(expandedItems, orgVocabRef.current);

        // Step 5: Set up sweep interval
        sweepIntervalRef.current = setInterval(() => {
          if (matchingEngineRef.current) {
            console.log('⏰ Running periodic transcript sweep...');
            const sweepMatches = matchingEngineRef.current.runFullTranscriptSweep();
            for (const match of sweepMatches) {
              markItemCoveredBySweep(match.itemId, match.confidence, match.matchedText);
            }
          }
        }, SWEEP_INTERVAL_MS);

        // Step 6: Set up max call time check
        const maxCallTimeMs = coachingSettings.maxCallTimeMin * 60 * 1000;
        if (maxCallTimeMs > 0) {
          maxCallTimeIntervalRef.current = setInterval(() => {
            const elapsed = Date.now() - (useSessionStore.getState().startedAt || Date.now());
            const remaining = maxCallTimeMs - elapsed;

            if (remaining <= 120000 && remaining > 0) {
              setMaxCallTimeWarning(true);
            }

            if (remaining <= 0) {
              console.log('⏰ Max call time reached, auto-ending session');
              handleEndSessionRef.current?.();
            }
          }, MAX_CALL_TIME_CHECK_MS);
        }

        // Step 6b: Set up idle timeout check
        lastSpeechTimestampRef.current = Date.now();
        idleTimeoutIntervalRef.current = setInterval(() => {
          const timeSinceLastSpeech = Date.now() - lastSpeechTimestampRef.current;

          if (timeSinceLastSpeech >= IDLE_TIMEOUT_MS - IDLE_WARNING_BEFORE_MS && timeSinceLastSpeech < IDLE_TIMEOUT_MS) {
            setIdleTimeoutWarning(true);
          }

          if (timeSinceLastSpeech >= IDLE_TIMEOUT_MS) {
            console.log('💤 Idle timeout reached (no speech for 5 minutes), auto-ending session');
            handleEndSessionRef.current?.();
          }
        }, IDLE_CHECK_INTERVAL_MS);

        // Step 7: Start speech service
        speechServiceRef.current = new DeepgramSpeechService(
          handleTranscriptSegment,
          handleSpeechError,
          handleConnectionStatus
        );

        // Broadcast state to popup
        setTimeout(() => {
          broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());
        }, 50);

        try {
          await speechServiceRef.current.start(authToken ?? undefined);
        } catch {
          // Error already handled in service
        }
      };

      initializeSession();
    }
  }, [status, agenda, isSignedIn, getToken, organization, handleTranscriptSegment, handleSpeechError, handleConnectionStatus, setConnectionStatus, markItemCoveredBySweep, setLlmExpansionUsed, post, setSessionId, coachingSettings.maxCallTimeMin, broadcast, isRehearsalMode, rehearsalSettings, setRehearsalSessionId]);

  useEffect(() => {
    if (matchingEngineRef.current && agenda) {
      matchingEngineRef.current.updateAgenda(agenda.items);
    }
  }, [agenda]);

  // Broadcast state updates to popup when agenda coverage changes
  useEffect(() => {
    if (status === 'listening' && agenda) {
      broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());
    }
  }, [agenda, status, broadcast]);

  // Broadcast state updates to popup when connectionStatus changes
  useEffect(() => {
    if (status === 'listening') {
      console.log('[Popup Sync] Broadcasting connectionStatus change:', connectionStatus);
      broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());
    }
  }, [connectionStatus, status, broadcast]);

  // Broadcast transcript updates to popup (throttled to every 500ms)
  const lastTranscriptBroadcastRef = useRef<number>(0);
  useEffect(() => {
    if (status === 'listening' && transcript.length > 0) {
      const now = Date.now();
      if (now - lastTranscriptBroadcastRef.current > 500) {
        lastTranscriptBroadcastRef.current = now;
        broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());
      }
    }
  }, [transcript, status, broadcast]);

  // Periodic state sync to popup every 2 seconds while session is active
  // This ensures popup stays in sync even if individual broadcasts are missed
  useEffect(() => {
    if (status !== 'listening' || !isPopupOpen()) return;

    const syncInterval = setInterval(() => {
      broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());
    }, 2000);

    // Initial broadcast
    broadcast('STATE_UPDATE', useSessionStore.getState().getSyncableState());

    return () => clearInterval(syncInterval);
  }, [status, broadcast]);

  // Stable callback for coaching alerts (prevent infinite re-render loop)
  const handleCoachingAlert = useCallback((message: string) => {
    console.log('Coaching alert:', message);
  }, []);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(400, Math.min(1400, startWidth + e.clientX - startX));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 50, startHeight + e.clientY - startY));
      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [size]);

  // Check if popup is open
  const popupIsOpen = isPopupOpen();

  // When popup is open and session is active, hide the full UI
  // But when popup is open and session is idle, show a minimal UI to start the session
  if (popupIsOpen && status === 'listening') {
    // Session is active, popup shows the UI - just keep this component mounted for processing
    return null;
  }

  if (popupIsOpen && status === 'idle') {
    // Popup is open but session hasn't started - show minimal UI to start session
    return (
      <Draggable
        handle=".widget-handle"
        nodeRef={nodeRef}
        bounds="parent"
        defaultPosition={{ x: 50, y: 80 }}
      >
        <div
          ref={nodeRef}
          className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 w-72"
          style={{ zIndex: 9999, opacity: 0.97, top: 0, left: 0 }}
        >
          <div className="widget-handle cursor-move bg-gray-50 rounded-t-xl px-4 py-2.5 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-gray-400 select-none">⋮⋮</span>
              <span className="font-semibold text-gray-700 text-sm">DemoPath</span>
            </div>
            <span className="text-xs text-blue-500">Popup Open</span>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-3">
              Session will appear in the popup window.
            </p>
            {agenda && agenda.items.length > 0 ? (
              <button
                onClick={handleStartSession}
                disabled={isExpanding}
                className="w-full py-2.5 px-4 rounded-lg font-medium bg-green-500 hover:bg-green-600 text-white transition-colors disabled:opacity-50"
              >
                {isExpanding ? 'Preparing...' : 'Start Session'}
              </button>
            ) : (
              <p className="text-sm text-amber-600">
                Enter an agenda above to start a session.
              </p>
            )}
          </div>
        </div>
      </Draggable>
    );
  }

  return (
    <>
    <Draggable
      handle=".widget-handle"
      nodeRef={nodeRef}
      bounds="parent"
      defaultPosition={{ x: 50, y: 80 }}
      disabled={isResizing}
    >
      <div
        ref={nodeRef}
        className={`
          fixed bg-white rounded-xl shadow-2xl border border-gray-200
          transition-all duration-200 ease-in-out flex flex-col
          ${minimized ? 'w-64' : ''}
        `}
        style={{
          zIndex: 9999,
          opacity: 0.97,
          top: 0,
          left: 0,
          width: minimized ? undefined : size.width,
          height: minimized ? undefined : Math.min(size.height, window.innerHeight - 100),
          maxHeight: window.innerHeight - 100,
        }}
      >
        {/* Header */}
        <div className="widget-handle cursor-move bg-gray-50 rounded-t-xl px-4 py-2.5 flex items-center justify-between border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 select-none">⋮⋮</span>
            <span className="font-semibold text-gray-700 text-sm">DemoPath</span>
            {connectionStatus === 'connected' && (
              <span className="w-2 h-2 bg-green-500 rounded-full" title="Connected" />
            )}
            {connectionStatus === 'connecting' && (
              <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" title="Connecting" />
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Launch in popup window button (only when not in session) */}
            {status === 'idle' && !isPopupOpen() && (
              <button
                onClick={() => openSessionPopup()}
                className="p-1 hover:bg-gray-200 rounded text-gray-500 text-xs"
                title="Launch in separate window"
              >
                ⧉
              </button>
            )}
            <button
              onClick={() => setMinimized(!minimized)}
              className="p-1 hover:bg-gray-200 rounded text-gray-500 text-xs"
            >
              {minimized ? '□' : '−'}
            </button>
          </div>
        </div>

        {!minimized && (
          <div
            className="p-4 overflow-y-auto flex-1"
            style={{
              maxHeight: `${size.height - 60}px`,
              overflowY: 'auto',
              overflowX: 'hidden'
            }}
          >
            {error && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start justify-between">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">✕</button>
              </div>
            )}

            {status === 'listening' && <StatusBar />}

            {/* Max call time warning */}
            {maxCallTimeWarning && status === 'listening' && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                <span className="text-lg">⏰</span>
                <span>
                  <strong>Approaching max call time.</strong> Session will auto-end in ~2 minutes.
                  <button
                    onClick={handleEndSession}
                    className="ml-2 underline hover:no-underline"
                  >
                    End now
                  </button>
                </span>
              </div>
            )}

            {/* Idle timeout warning */}
            {idleTimeoutWarning && status === 'listening' && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm flex items-center gap-2">
                <span className="text-lg">💤</span>
                <span>
                  <strong>No speech detected for a while.</strong> Session will auto-end in ~1 minute to save resources.
                  <button
                    onClick={handleEndSession}
                    className="ml-2 underline hover:no-underline"
                  >
                    End now
                  </button>
                </span>
              </div>
            )}

            {status === 'idle' && !showSetup && (
              <>
                <AgendaInput />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setShowSetup(true)}
                    disabled={!agenda || agenda.items.length === 0}
                    className="px-4 py-2.5 rounded-lg font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Configure duration, reminders, and alerts"
                  >
                    Setup
                  </button>
                  <button
                    onClick={handleStartSession}
                    disabled={!agenda || agenda.items.length === 0 || isExpanding}
                    className={`
                      flex-1 py-2.5 px-4 rounded-lg font-medium transition-colors
                      ${agenda && agenda.items.length > 0 && !isExpanding
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                    `}
                  >
                    {isExpanding ? 'Preparing...' : 'Start Session'}
                  </button>
                </div>
              </>
            )}

            {status === 'idle' && showSetup && (
              <div className="space-y-4">
                {/* Rehearsal Mode Toggle */}
                <RehearsalSetup
                  onToggleMode={() => {
                    // Mode toggle is handled by the component via store
                  }}
                  isRehearsalMode={isRehearsalMode}
                />

                {/* Session Settings (only for Live Call mode) */}
                {!isRehearsalMode && (
                  <SessionSetup
                    onContinue={() => setShowSetup(false)}
                    onSkip={() => setShowSetup(false)}
                  />
                )}

                {/* Actions for Rehearsal Mode */}
                {isRehearsalMode && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setShowSetup(false)}
                      className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleStartSession}
                      disabled={!agenda || agenda.items.length === 0 || isExpanding}
                      className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isExpanding ? 'Preparing...' : 'Start Rehearsal'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {status === 'listening' && (
              <>
                {/* Session Preparing Indicator - shows during initialization */}
                {(isExpanding || connectionStatus === 'connecting') && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                      <div>
                        <p className="font-medium text-blue-900">Preparing your session...</p>
                        <p className="text-sm text-blue-600">
                          {isExpanding ? 'Analyzing your agenda for better matching' : 'Connecting to speech recognition'}
                        </p>
                      </div>
                    </div>
                    {/* Progress steps */}
                    <div className="flex items-center gap-2 text-xs">
                      <div className={`flex items-center gap-1 ${!isExpanding ? 'text-green-600' : 'text-blue-600'}`}>
                        {!isExpanding ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        )}
                        <span>Agenda analysis</span>
                      </div>
                      <span className="text-gray-300">→</span>
                      <div className={`flex items-center gap-1 ${connectionStatus === 'connected' ? 'text-green-600' : isExpanding ? 'text-gray-400' : 'text-blue-600'}`}>
                        {connectionStatus === 'connected' ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : !isExpanding ? (
                          <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span className="w-3 h-3 border border-gray-300 rounded-full" />
                        )}
                        <span>Speech recognition</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rehearsal Mode: Question Generation Indicator */}
                {isRehearsalMode && isGeneratingQuestion && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <span>Generating practice question...</span>
                  </div>
                )}

                {/* Rehearsal Mode: Current Question */}
                {isRehearsalMode && currentQuestion && (
                  <div className="mb-4">
                    <QuestionCard
                      question={currentQuestion}
                      onSubmit={submitAnswer}
                      onSkip={skipQuestion}
                      onOpenTypedAnswer={() => setShowTypedAnswerModal(true)}
                      isEvaluating={isEvaluatingAnswer}
                      timerDurationMs={
                        rehearsalSettings?.difficulty === 'medium' ? 15000
                        : rehearsalSettings?.difficulty === 'hard' ? 10000
                        : null
                      }
                      isListeningForVoice={isListeningForAnswer}
                      voiceTranscript={answerTranscriptBuffer}
                    />
                    {/* Dismiss button for answered questions */}
                    {currentQuestion.respondedAt && !currentQuestion.skipped && (
                      <button
                        onClick={dismissQuestionResult}
                        className="w-full mt-2 py-2 px-4 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        Continue Practicing
                      </button>
                    )}
                  </div>
                )}

                {/* Coaching Panel (hide in rehearsal mode to reduce clutter) */}
                {!isRehearsalMode && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <CoachingPanel onAlert={handleCoachingAlert} />
                  </div>
                )}

                {/* Rehearsal Mode: Progress indicator */}
                {isRehearsalMode && questionHistory.length > 0 && (
                  <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between text-sm">
                    <span className="text-blue-700">
                      Questions answered: {questionHistory.filter(q => q.respondedAt && !q.skipped).length}
                    </span>
                    <span className="text-blue-600">
                      Skipped: {questionHistory.filter(q => q.skipped).length}
                    </span>
                  </div>
                )}

                {/* Agenda Panel */}
                <AgendaPanel />

                <button
                  onClick={handleEndSession}
                  className="w-full mt-4 py-2.5 px-4 rounded-lg font-medium bg-red-500 hover:bg-red-600 text-white transition-colors"
                >
                  {isRehearsalMode ? 'End Rehearsal' : 'End Session'}
                </button>
              </>
            )}

            {status === 'complete' && (
              <>
                {isRehearsalMode ? (
                  <>
                    {/* Rehearsal Summary */}
                    <RehearsalSummary
                      questions={questionHistory}
                      overallScore={
                        questionHistory.filter(q => q.score !== undefined).length > 0
                          ? questionHistory
                              .filter(q => q.score !== undefined)
                              .reduce((sum, q) => sum + (q.score ?? 0), 0) /
                            questionHistory.filter(q => q.score !== undefined).length
                          : null
                      }
                      onPracticeAgain={() => {
                        disableRehearsalMode();
                        handleReset();
                      }}
                    />
                  </>
                ) : (
                  <>
                    <SessionSummary />
                    <button
                      onClick={handleReset}
                      className="w-full mt-4 py-2.5 px-4 rounded-lg font-medium bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                    >
                      New Session
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {minimized && status === 'listening' && (
          <div className="px-4 py-2 flex items-center justify-between">
            <StatusBar minimal />
            <button
              onClick={handleEndSession}
              className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            >
              End
            </button>
          </div>
        )}

        {/* Resize Handle */}
        {!minimized && (
          <div
            onMouseDown={handleResizeStart}
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize flex items-center justify-center text-gray-300 hover:text-gray-500"
            style={{ fontSize: '10px' }}
          >
            ⊿
          </div>
        )}
      </div>
    </Draggable>

    {/* Typed Answer Modal for Rehearsal Mode */}
    {isRehearsalMode && currentQuestion && !currentQuestion.respondedAt && (
      <AnswerInputModal
        isOpen={showTypedAnswerModal}
        onClose={() => setShowTypedAnswerModal(false)}
        onSubmit={async (answer) => {
          await submitAnswer(answer, 'typed');
          setShowTypedAnswerModal(false);
        }}
        questionText={currentQuestion.questionText}
        isSubmitting={isEvaluatingAnswer}
        timeRemainingMs={
          rehearsalSettings?.difficulty !== 'easy'
            ? useSessionStore.getState().getQuestionTimeRemaining?.() ?? null
            : null
        }
      />
    )}
    </>
  );
}
